import type React from 'react';
import { useEffect, useState } from 'react';

import {
  AsyncKovResourcePicker,
  KovResourcePicker,
  Modal,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import { contentApi } from '@store/api/apiConfig';

import type { ContentHref, Facet } from '@generalTypes/apiTypes';
import { useTypedSelector } from '../../hooks/useTypedSelector';

export interface FacetModalProps {
  modalTitle?: string;
  facet?: Facet;
  facetComponentOptions?: Array<{ key: string; label: string }>;
  onClose?: () => void;
  onSubmit?: (facet: Facet) => void;
}

const FacetEditModal: React.FC<FacetModalProps> = ({
  modalTitle = '',
  facet,
  facetComponentOptions = [],
  onClose = () => {},
  onSubmit,
}) => {
  const sriLoadOptionsFn = useLoadSriOptions({
    sriClient: contentApi,
    href: '/content',
    parameters: {
      type: 'REFERENCE_FRAME',
    },
  });

  const [facetForm, setFacetForm] = useState<
    Partial<Facet & { source: { href: ContentHref } | null }>
  >(facet || {});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    emptyLabel: false,
    emptyComponent: false,
    emptySource: false,
  });

  const selectedReferenceFrame = useTypedSelector(
    (state) =>
      facet &&
      'source' in facet &&
      facet.source.href &&
      state.referenceFrames[facet.source.href]?.content[facet.source.href],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFacetForm((prevFacet) => ({ ...prevFacet, [name]: value }));
  };

  const handleKovResourcePickerChange = (value) => {
    setFacetForm((prevFacet) => ({ ...prevFacet, component: value.key }));
  };

  const handleAsyncKovResourcePickerChange = (value) => {
    if (value) {
      setFacetForm((prevFacet) => ({ ...prevFacet, source: { href: value.href } }));
    } else {
      // @ts-expect-error - value is null
      setFacetForm((prevFacet) => ({ ...prevFacet, source: null }));
    }
  };

  useEffect(() => {
    if (submitted) {
      validate(facetForm);
    }
  }, [facetForm, submitted]);

  const handleSubmit = () => {
    setSubmitted(true);
    const isValid = validate(facetForm);

    if (onSubmit && isValid) {
      onSubmit(facetForm as Facet);
      onClose();
    }
  };

  const validate = (form: Partial<Facet>) => {
    const validationErrors = {
      emptyLabel: !form?.label,
      emptyComponent: !form?.component,
      emptySource: form?.component === 'SELECT_FROM_REFERENCE_FRAME' && !form?.source,
    };
    setErrors(validationErrors);
    return (
      !validationErrors.emptyLabel &&
      !validationErrors.emptyComponent &&
      !validationErrors.emptySource
    );
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title={modalTitle} onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label>Label</label>
              <input
                type="text"
                name="label"
                defaultValue={facetForm?.label}
                onChange={handleChange}
                className="form-control"
              />
              {errors.emptyLabel && <small className="label-error">Label is verplicht</small>}
            </div>
          </div>
        </div>

        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label>Component</label>
              <KovResourcePicker
                localData={facetComponentOptions}
                display="label"
                defaultValue={facetComponentOptions.find((z) => z.key === facetForm?.component)}
                clearable={false}
                disabled={false}
                disableSearch={true}
                menuPlacement="bottom"
                style={{ option: (provided) => ({ ...provided, cursor: 'pointer' }) }}
                onChange={handleKovResourcePickerChange}
              />
              {errors.emptyComponent && (
                <small className="label-error">Selecteer een component</small>
              )}
            </div>
          </div>
        </div>

        {facetForm?.component === 'SELECT_FROM_REFERENCE_FRAME' && (
          <div className="form-group">
            <div className="row">
              <div className="col-md-12">
                <label>Bron</label>
                <AsyncKovResourcePicker
                  defaultValue={selectedReferenceFrame}
                  loadOptions={sriLoadOptionsFn}
                  clearable={true}
                  disabled={false}
                  onChange={handleAsyncKovResourcePickerChange}
                  placeholder="Zoek een ordeningskader"
                  multiple={false}
                  menuPlacement="bottom"
                  optionValue={(o) => o.key}
                  optionTemplate={(o) => o.title || o.$$expanded.title}
                />
                {errors.emptySource && <small className="label-error">Selecteer een bron</small>}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={handleSubmit}>
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

FacetEditModal.displayName = 'FacetEditModal';

export default FacetEditModal;
